import { formatNumberWithSeparators } from "@monthly/charts";
import { TimelineRounded } from "@mui/icons-material";
import { Card, Divider, Stack, styled, Typography } from "@mui/material";

// Unit options for display
const UNIT_OPTIONS = ["K", "M", "B"] as const;

const Container = styled(Card)(({ theme }) => ({
  gap: "15px",
  alignSelf: "stretch",
  borderRadius: "12px",
  border: `1px solid ${theme.palette.support[300]}`,
  transition: "all 0.2s ease-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[5],
  },
}));

export interface DataCardProps {
  label: string;
  value: number;
  period: string;
  currencySymbol: string;
  units: (typeof UNIT_OPTIONS)[number];
  lineColor?: string;
  frequency: string;
  currency: string;
  comparable: string;
  analysis: string;
  decimals?: number;
}

export function DataCard({
  label,
  value,
  period,
  currencySymbol,
  units,
  lineColor,
  frequency,
  currency,
  comparable,
  analysis,
  decimals = 2,
}: DataCardProps) {
  // Format the value based on decimals prop
  const formattedValue = formatNumberWithSeparators(value, decimals);

  return (
    <Container key={label} elevation={2}>
      <Stack p="20px" gap={2}>
        <Stack gap={1}>
          <Stack flexDirection="row" alignItems="center" gap={1}>
            <TimelineRounded color="secondary" fontSize="small" />
            <Typography variant="h5">{label}</Typography>
          </Stack>
          <Typography variant="overline">
            {frequency} | {period} | {comparable} | {currency} | {analysis}
          </Typography>
        </Stack>
        <Divider
          sx={(theme) => ({ borderColor: lineColor || theme.palette.divider })}
        />
        <Stack flexDirection="row" justifyContent="space-between">
          <Stack>
            <Typography
              variant="body1"
              sx={(theme) => ({
                color: theme.palette.secondary.dark,
              })}
            >
              {period}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center">
            <Typography variant="body1" component="span">
              {currencySymbol}
            </Typography>
            <Typography variant="h2">{formattedValue}</Typography>
            <Typography variant="body1" component="span">
              {units}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Container>
  );
}
